import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuthStore } from './stores/useAuthStore';
import { useFinanceStore } from './stores/useFinanceStore';
import { useLocation, useNavigate, Navigate, Routes, Route } from 'react-router-dom';
import { SocketProvider } from './components/chat/v3/SocketProvider';
import { Toaster } from 'react-hot-toast';
import GlobalModals from './components/modals/GlobalModals';
import PageLoader from './components/common/PageLoader';
import SplashScreen from './components/common/SplashScreen';
import HomePageSkeleton from './components/skeletons/HomePageSkeleton';
import ScrollToTopButton from './components/common/ScrollToTopButton';
import MiniAudioPlayer from './components/podcast/MiniAudioPlayer';
import NotificationListener from './components/common/NotificationListener';
import SmartNavigation from './components/common/SmartNavigation';
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
import MainLayout from './components/layouts/MainLayout';
import FullWidthLayout from './components/layouts/FullWidthLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedRouteAdmin from './components/auth/ProtectedRouteAdmin';
import StudioLayout from './components/layouts/StudioLayout';
import TierProtectedRoute from './components/auth/TierProtectedRoute';

const AdminLayout = lazy(() => import('./components/layouts/AdminLayout'));
const StaffFinancialsPage = lazy(() => import('./pages/admin/StaffFinancialsPage'));
const WalletManagement = lazy(() => import('./pages/admin/WalletManagement'));
const AdminRootProfile = lazy(() => import('./pages/admin/AdminRootProfile'));
const AdminControlCenter = lazy(() => import('./pages/admin/AdminControlCenter'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminBusinessMaster = lazy(() => import('./pages/admin/AdminBusinessMaster'));
const AdminFinancesMonitor = lazy(() => import('./pages/admin/AdminFinancesMonitor'));
const AdminPosts = lazy(() => import('./pages/admin/AdminPosts'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));

// Keep other page imports as they are...
const AccountVerificationPage = lazy(() => import('./pages/AccountVerificationPage'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));
const AdBuilderPage = lazy(() => import('./pages/admin/AdBuilderPage'));
const AdsCenterPage = lazy(() => import('./pages/AdsCenterPage'));
const AdminApprovalsPage = lazy(() => import('./pages/admin/AdminApprovalsPage'));
const AdminMyProfilePage = lazy(() => import('./pages/admin/AdminMyProfilePage'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const AdminStoreDashboardPage = lazy(() => import('./pages/admin/AdminStoreDashboardPage'));
const AdminSupportTicketDetailPage = lazy(() => import('./pages/admin/AdminSupportTicketDetailPage'));
const AdminUserDetailsPage = lazy(() => import('./pages/admin/AdminUserDetailsPage'));
const AdminSupportTicketsPage = lazy(() => import('./pages/admin/AdminSupportTicketsPage'));
const AdminInboxPage = lazy(() => import('./pages/admin/AdminInboxPage'));
const AgoraCentralPage = lazy(() => import('./pages/AgoraCentralPage'));
const AgoraPollsPage = lazy(() => import('./pages/AgoraPollsPage'));
const AlbumPage = lazy(() => import('./pages/AlbumPage'));
const AllNewsPage = lazy(() => import('./pages/AllNewsPage'));
const ArticleManagementPage = lazy(() => import('./pages/admin/ArticleManagementPage'));
const ArticlesPage = lazy(() => import('./pages/ArticlePage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const BloggerPage = lazy(() => import('./pages/BloggerPage'));
const BloggersListPage = lazy(() => import('./pages/BloggersListPage'));
const BlocksExplorerPage = lazy(() => import('./pages/BlocksExplorerPage'));
const CallPage = lazy(() => import('./pages/CallPage'));
const CampaignDetailsPage = lazy(() => import('./pages/CampaignDetailsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const ChatContainer = lazy(() => import('./components/chat/v3/ChatContainer'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ClipsPage = lazy(() => import('./pages/ClipsPage'));
const CompaniesNearbyPage = lazy(() => import('./pages/CompaniesNearbyPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const CompanyPlansPage = lazy(() => import('./pages/CompanyPlansPage'));
const CreateArticlePage = lazy(() => import('./pages/CreateArticlePage'));
const CreateCompanyPage = lazy(() => import('./pages/CreateCompanyPage'));
const CreateJobPage = lazy(() => import('./pages/CreateJobPage'));
const CreatePageType = lazy(() => import('./pages/CreatePageType'));
const CreateStorePage = lazy(() => import('./pages/CreateStorePage'));
const CreationPage = lazy(() => import('./pages/CreationPage'));
const CreatorAnalytics = lazy(() => import('./components/common/CreatorAnalytics'));
const CreatorApplicationPage = lazy(() => import('./pages/CreatorApplicationPage'));
const EditArticlePage = lazy(() => import('./pages/EditArticlePage'));
const EditCompanyPage = lazy(() => import('./pages/EditCompanyPage'));
const EditCompanyProfilePage = lazy(() => import('./pages/EditCompanyProfilePage'));
const EditJobPage = lazy(() => import('./pages/EditJobPage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const EditStorePage = lazy(() => import('./pages/EditStorePage'));
const EmployerApplicationsPage = lazy(() => import('./pages/EmployerApplicationsPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const JobApplicantsPage = lazy(() => import('./pages/JobApplicantsPage'));
const JobDetailsPage = lazy(() => import('./pages/JobDetailsPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const LiveStudioPage = lazy(() => import('./pages/LiveStudioPage'));
const LiveViewerPage = lazy(() => import('./pages/LiveViewerPage'));
const MusicPage = lazy(() => import('./pages/MusicPage'));
const MyAdsPage = lazy(() => import('./pages/MyAdsPage'));
const MyDownloadsPage = lazy(() => import('./pages/MyDownloadsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const PassesManagementPage = lazy(() => import('./pages/admin/PassesManagementPage'));
const PodcastDiscoveryPage = lazy(() => import('./pages/PodcastDiscoveryPage'));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const PostsManagementPage = lazy(() => import('./components/ads/PostsManagementPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProfessionalProfilePage = lazy(() => import('./pages/ProfessionalProfilePage'));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage'));
const ResetPasswordPage = lazy(() => import('./components/common/ResetPasswordPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const StorePage = lazy(() => import('./pages/StorePage'));
const StoresListPage = lazy(() => import('./pages/StoresListPage'));
const SurveyManagementPage = lazy(() => import('./pages/admin/SurveyManagementPage'));
const SurveyPage = lazy(() => import('./pages/SurveyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const UploadPodcastPage = lazy(() => import('./pages/UploadPodcastPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const VideoManagementPage = lazy(() => import('./pages/admin/VideoManagementPage'));
const AdsManagementPage = lazy(() => import('./pages/admin/AdsManagementPage'));
const AdminPressControl = lazy(() => import('./pages/admin/AdminPressControl'));
const AddProductPage = lazy(() => import('./pages/AddProductPage'));
const PostSinglePage = lazy(() => import('./pages/PostSinglePage'));

const AppContent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useAuthStore((state) => state.currentUser);

    // 🟢 FIX: Solo ejecutar cuando currentUser realmente cambia
    useEffect(() => {
        if (currentUser) {
            useFinanceStore.getState().fetchSubscription();
        }
    }, [currentUser?._id]);

    // 🟢 FIX: Solo ejecutar cuando currentUser o ruta cambian
    useEffect(() => {
        if (currentUser && location.pathname === '/') {
            const adminRoles = ['admin', 'technician', 'admin_root', 'moderator', 'editor'];
            const hasAdminRole = currentUser.roles?.some(r => adminRoles.includes(r)) || adminRoles.includes(currentUser.role);
            if (hasAdminRole) {
                navigate('/admin/dashboard');
            }
        }
    }, [currentUser?._id, location.pathname]);

    return (
        <>
            <RoutesComponent />
            <ScrollToTopButton />
            <MiniAudioPlayer />
            <NotificationListener />
            <SmartNavigation />
        </>
    );
};

const App = () => {
    // 🔴 FIX CRÍTICO #185: Selectores individuales (NO objeto)
    const checkAuthStatus = useAuthStore((state) => state.checkAuthStatus);
    const isLoading = useAuthStore((state) => state.isLoading);
    const isVerifying = useAuthStore((state) => state.isVerifying);
    const currentUser = useAuthStore((state) => state.currentUser);
    const [showSplash, setShowSplash] = useState(true);

    useEffect(() => {
        // Simular tiempo de carga del splash screen
        const timer = setTimeout(() => {
            setShowSplash(false);
        }, 2500); // 2.5 segundos de impacto visual
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    if (showSplash) {
        return <SplashScreen />;
    }

    // 🔴 VERIFICACIÓN SUAVE: Mostrar skeleton mientras verificamos sesión
    if (isVerifying) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
                <HomePageSkeleton />
            </div>
        );
    }

    // Bypass de Carga: Si ya hay usuario, no bloqueamos la UI aunque isLoading sea true
    if (isLoading && !currentUser) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
                <HomePageSkeleton />
            </div>
        );
    }

    return (
        <SocketProvider> {/* 🟢 SocketProvider ahora está dentro del Router definido en main.jsx */}
            <ToasterComponent />
            <GlobalModals />
            <AppContent />
            {/* 🟢 NUEVO: Indicador de conexión para Render.com */}
        </SocketProvider>
    );
};

// 🟢 Extraemos Toaster y Routes a sus propios componentes para mantener App limpio
const ToasterComponent = () => (
    <Toaster 
        position="top-right"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
            className: 'text-sm',
            duration: 5000,
            style: {
                background: '#ff9900ff',
                color: '#03000fff',
            },
        }}
    />
);

const StudioRedirect = () => {
    const currentUser = useAuthStore((state) => state.currentUser);
    if (!currentUser?.username) {
        return <Navigate to="/auth" replace />;
    }
    return <Navigate to={`/blogger/${currentUser.username}`} replace />;
};

const RoutesComponent = () => (
    <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} /> {/* 🟢 Ruta de verificación */}
            <Route path="/verify-account/:token" element={<AccountVerificationPage />} /> {/* 🟢 Ruta de acción de verificación */}
            <Route path="/settings" element={<SettingsPage />} /> {/* 🟢 Ruta de configuración */}

        {/* --- RUTAS PÚBLICAS --- */}
        <Route element={<MainLayout />}>
            {/* La página de aterrizaje ahora es HomePage */}
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/ads-center" element={<AdsCenterPage />} />
            <Route path="/stores" element={<StoresListPage />} />
            <Route path="/store/:storeName" element={<StorePage />} />
                <Route path="/store/:storeId/dashboard" element={<AdminStoreDashboardPage />} />
            <Route path="/company/:companyName" element={<CompanyPage />} />
            <Route path="/companies/nearby" element={<CompaniesNearbyPage />} /> {/* 🔒 PUERTA B: Geolocalización solo Senador */}
            <Route path="/employer/applications" element={<EmployerApplicationsPage />} /> {/* 🔥 PUERTA C: Dashboard RR.HH. */}
            {/* 🟢 CORRECCIÓN: Apuntamos /jobs a la página correcta y unificada */}
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/create" element={<CreateJobPage />} />
            <Route path="/jobs/:id" element={<JobDetailsPage />} />
            <Route path="/post/:postId" element={<PostSinglePage />} />
            {/* Páginas estáticas */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/help" element={<HelpPage />} />
            {/* 🟢 SOLUCIÓN: Movemos la página del artículo al MainLayout para que herede la sidebar. */}
        </Route>

        {/* Rutas públicas de ancho completo */}
        <Route element={<FullWidthLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/news/:category?" element={<AllNewsPage />} />
            {/* 🟢 SOLUCIÓN: Ruta pública para la encuesta */}
            <Route path="/surveys/:surveyId" element={<SurveyPage />} />
            {/* 🟢 SOLUCIÓN: Ruta pública para ver una transmisión */}
                        <Route path="/live/:username" element={
                            <RouteErrorBoundary>
                                <LiveViewerPage />
                            </RouteErrorBoundary>
                        } />
            <Route path="/agora-central" element={<AgoraCentralPage />} /> {/* 🟢 Nueva ruta Ágora Central */}
            <Route path="/agora/polls" element={<AgoraPollsPage />} /> {/* 🔥 PUERTA E: Votaciones tier-gated */}
        </Route>

        <Route path="/podcasts" element={<PodcastDiscoveryPage />} />
        <Route path="/blocks" element={<BlocksExplorerPage />} /> {/* 🟢 Nueva ruta Ágora Elite */}

        {/* --- RUTAS PROTEGIDAS --- */}
        <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
                <Route path="/bloggers" element={<BloggersListPage />} /> 
                <Route path="/blogger/:username" element={<BloggerPage />} />
                {/* El Feed ahora es una ruta protegida */}
                <Route path="/feed" element={<FeedPage />} />
                <Route path="/messages" element={<Navigate to="/messenger" replace />} />
                <Route path="/chat" element={<Navigate to="/messenger" replace />} />
                <Route path="/chat/:id" element={<Navigate to="/messenger" replace />} />
                                <Route path="/messenger" element={
                                    <RouteErrorBoundary>
                                        <ChatContainer mode="user" />
                                    </RouteErrorBoundary>
                                } />
                <Route path="/profile/:username" element={<ProfilePage />} />
                <Route path="/profile/:username/edit" element={<EditProfilePage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/order-success/:orderId" element={<OrderSuccessPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/article/create" element={<CreateArticlePage />} />
                {/* 🟢 SOLUCIÓN: Añadimos la ruta para editar artículos. */}
                <Route path="/creator/apply" element={<CreatorApplicationPage />} /> {/* 🟢 Nueva ruta para aplicar a creador */}
                <Route path="/creation/create" element={<CreationPage />} />
                <Route path="/profile/:username/photos" element={<AlbumPage />} />
                <Route path="/profile/:username/articles" element={<ArticlesPage />} />
                <Route path="/profile/:username/clips" element={<ClipsPage />} />
                <Route path="/profile/:username/music" element={<MusicPage />} />
                <Route path="/campaign/:campaignId" element={<CampaignDetailsPage />} />
                {/* 🟢 CORRECCIÓN: Movemos la ruta aquí para que herede el layout principal. */}
                <Route path="/my-profile/professional" element={<ProfessionalProfilePage />} />
                <Route path="/my-orders" element={<MyOrdersPage />} /> {/* 💼 Dashboard de pedidos */}
                <Route path="/my-ads" element={<MyAdsPage />} />
                <Route path="/jobs/create" element={<CreateJobPage />} /> {/* NEW */}
                {/* 🟢 SOLUCIÓN: Añadimos la nueva ruta para el feed de actividad. */}
                <Route path="/activity" element={<ActivityFeedPage />} />
                <Route path="/jobs/:id/applicants" element={<JobApplicantsPage />} /> {/* NEW */}
                <Route path="/jobs/edit/:id" element={<EditJobPage />} /> {/* NEW */}
                <Route path="/podcast/upload" element={<UploadPodcastPage />} /> {/* 🟢 Nueva ruta para subir podcasts */}
                <Route path="/podcast/:podcastId" element={<PodcastPage />} /> {/* 🟢 Nueva ruta para el reproductor de podcast */}
                <Route path="/my-downloads" element={<MyDownloadsPage />} /> {/* 🟢 Nueva ruta para mis descargas */}
                <Route path="/creator/analytics" element={<CreatorAnalytics />} /> {/* 🟢 Nueva ruta para estadísticas */}
                                <Route path="/call/:roomId" element={
                                    <RouteErrorBoundary>
                                        <CallPage />
                                    </RouteErrorBoundary>
                                } /> {/* 🟢 Ruta de llamadas */}
            </Route>
                <Route path="/article/edit/:articleId" element={<EditArticlePage />} />
            <Route path="/studio" element={<StudioRedirect />} />

            <Route element={<StudioLayout />}>
                                <Route path="/live/studio" element={
                                    <RouteErrorBoundary>
                                        <LiveStudioPage />
                                    </RouteErrorBoundary>
                                } />
            </Route>
               
            
            <Route path="/pages/create" element={<CreatePageType />} />
            <Route path="/company/create" element={
              <TierProtectedRoute requiredTier="senador">
                <CreateCompanyPage />
              </TierProtectedRoute>
            } />
            <Route path="/company/:companyName/edit" element={<EditCompanyPage />} />
            <Route path="/company/:companyName/edit-profile" element={<EditCompanyProfilePage />} />
            <Route path="/company/:companyName/plans" element={<CompanyPlansPage />} />
            <Route path="/store/create" element={
              <TierProtectedRoute requiredTier="senador">
                <CreateStorePage />
              </TierProtectedRoute>
            } />
            <Route path="/store/:storeName/add-product" element={<AddProductPage />} />
            <Route path="/store/:storeName/edit" element={<EditStorePage />} />
        </Route>

        <Route element={<ProtectedRouteAdmin />}>
            <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminControlCenter />} />
                <Route path="system-settings" element={<SystemSettings />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="business" element={<AdminBusinessMaster />} />
                <Route path="finances" element={<AdminFinancesMonitor />} />
                <Route path="wallet" element={<WalletManagement />} />
                <Route path="posts" element={<AdminPosts />} />
                {/* Manteniendo algunas rutas existentes que parecen útiles */}
                <Route path="my-profile" element={<AdminMyProfilePage />} />
                <Route path="users/:userId" element={<AdminUserDetailsPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="approvals" element={<AdminApprovalsPage />} />
                <Route path="users-management" element={<UserManagement />} />
                <Route path="support-tickets" element={<AdminSupportTicketsPage />} />
                                <Route path="messenger" element={
                                    <RouteErrorBoundary>
                                        <ChatContainer mode="admin" />
                                    </RouteErrorBoundary>
                                } />
                <Route path="inbox" element={<AdminInboxPage />} />
                <Route path="press" element={<AdminPressControl />} />
                <Route path="ads" element={<AdsManagementPage />} />
                <Route path="staff-financials" element={<StaffFinancialsPage />} />
                <Route path="root-profile" element={<AdminRootProfile />} />
            </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    </Suspense>
);

export default App;